import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { formatKickoffDateTime, matchDayKey } from '../utils/datetime';
import { resolveMatchBucket, kickoffMs } from '../utils/matchStatus';
import { preloadExternalUrls } from '../utils/imagePreload';
import { trackEvent, Events } from '../utils/webviewBridge';
import './HomePage.css';

const HomePage = () => {
  const { t, locale } = useLocale();
  const [activeTab, setActiveTab] = useState('All');
  const [selectedLanguage] = useState(() => localStorage.getItem('vfootball-language') || 'EN');
  const [userTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [starredMatches, setStarredMatches] = useState(() => {
    const saved = localStorage.getItem('vfootball-starred');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState('today');
  const [leagueFilter, setLeagueFilter] = useState('all');
  const matchesPerPage = 15;
  const navigate = useNavigate();
  const todayKey = new Date().toISOString().slice(0, 10);
  const [showStarBtn, setShowStarBtn] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 769px)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const sync = () => setShowStarBtn(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Ограничиваем стартовую загрузку, чтобы главная не ждала огромный каталог.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const rangeFromIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 45);
    return d.toISOString().slice(0, 10);
  }, []);

  const filterLabels = {
    All: t.home.filters.all,
    Live: t.home.filters.live,
    Soon: t.home.filters.soon,
    Finished: t.home.filters.finished
  };

  const handleWatchMatch = (matchId, source = 'list') => {
    trackEvent(Events.MATCH_OPENED, { match_id: matchId, source: `home_${source}` });
    navigate(`/match/${matchId}`);
  };

  const handleStarMatch = (matchId) => {
    setStarredMatches((prev) => {
      const next = new Set(prev);
      const wasStarred = next.has(matchId);
      if (wasStarred) next.delete(matchId);
      else next.add(matchId);
      trackEvent(wasStarred ? Events.MATCH_UNSTARRED : Events.MATCH_STARRED, {
        match_id: matchId
      });
      localStorage.setItem('vfootball-starred', JSON.stringify(Array.from(next)));
      return next;
    });
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    trackEvent(Events.HOME_FILTER_CHANGED, { filter: 'tab', value: tab });
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const data = await apiService.getLeagueMatches({
        language: selectedLanguage,
        all_languages: 'true',
        range: 'catalog',
        from: rangeFromIso,
        to: todayIso
      });

      if (data.success && data.leagues) {
        setMatches(
          data.leagues.map((entry) => {
            const meta = entry.league || entry;
            return {
              league: {
                name: meta.name || 'League',
                logo: meta.logo || null,
                flag: meta.flag || null,
                country: meta.country || null
              },
              leagueFlag: meta.flag || null,
              matches: entry.matches || []
            };
          })
        );
        setError(null);
      } else {
        setError(data.error || t.home.fetchError);
      }
    } catch (err) {
      setError(t.home.connectionError);
      console.error('Error fetching matches:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!matches.length) return;
    const logos = matches.flatMap((group) =>
      (group.matches || []).flatMap((m) => [
        m.teams?.home?.logo,
        m.teams?.away?.logo,
        m.league?.logo,
        group.league?.logo
      ])
    );
    preloadExternalUrls(logos, { limit: 24 });
  }, [matches]);

  useEffect(() => {
    fetchMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allMatches = useMemo(
    () =>
      matches.flatMap((leagueGroup) =>
        leagueGroup.matches.map((match) => {
          const normalizedStatus = resolveMatchBucket(match);
          return {
            ...match,
            league: {
              ...(match.league || {}),
              name: match.league?.name || leagueGroup.league?.name,
              logo: match.league?.logo || leagueGroup.league?.logo || null,
              flag: match.league?.flag || leagueGroup.league?.flag || null
            },
            leagueLogo: match.league?.logo || leagueGroup.league?.logo || null,
            isStarred: starredMatches.has(match.id),
            normalizedStatus,
            isLive: normalizedStatus === 'live',
            dayKey: matchDayKey(match.fixture?.date || match.date || match.time)
          };
        })
      ),
    [matches, starredMatches]
  );

  // Pick up catalog score/status updates every 5 minutes
  useEffect(() => {
    const id = setInterval(() => {
      fetchMatches();
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableDates = useMemo(() => {
    const set = new Set(allMatches.map((m) => m.dayKey).filter(Boolean));
    return Array.from(set).sort();
  }, [allMatches]);

  const availableLeagues = useMemo(() => {
    const map = new Map();
    allMatches.forEach((m) => {
      const name = m.league?.name || 'League';
      if (!map.has(name)) map.set(name, m.league?.logo || null);
    });
    return Array.from(map.entries())
      .map(([name, logo]) => ({ name, logo }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allMatches]);

  const filteredMatches = useMemo(() => {
    const rank = { live: 0, scheduled: 1, finished: 2 };
    return allMatches
      .filter((match) => {
        if (activeTab === 'Live' && match.normalizedStatus !== 'live') return false;
        if (activeTab === 'Soon' && match.normalizedStatus !== 'scheduled') return false;
        if (activeTab === 'Finished' && match.normalizedStatus !== 'finished') return false;
        if (dateFilter === 'today' && match.dayKey !== todayKey) return false;
        if (dateFilter !== 'all' && dateFilter !== 'today' && match.dayKey !== dateFilter) return false;
        if (leagueFilter !== 'all' && (match.league?.name || '') !== leagueFilter) return false;
        return true;
      })
      .sort((a, b) => {
        if (a.isStarred && !b.isStarred) return -1;
        if (!a.isStarred && b.isStarred) return 1;

        if (activeTab === 'All') {
          const dr = rank[a.normalizedStatus] - rank[b.normalizedStatus];
          if (dr !== 0) return dr;
        }

        const ta = kickoffMs(a);
        const tb = kickoffMs(b);

        // Finished: newest first; Live/Soon/All: soonest / chronological
        if (activeTab === 'Finished' || a.normalizedStatus === 'finished') {
          if (a.normalizedStatus === 'finished' && b.normalizedStatus === 'finished') {
            return tb - ta;
          }
        }
        return ta - tb;
      });
  }, [allMatches, activeTab, dateFilter, leagueFilter, todayKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateFilter, leagueFilter, activeTab]);

  const getTimezoneAbbr = () => {
    try {
      return (
        new Intl.DateTimeFormat('en', {
          timeZoneName: 'short',
          timeZone: userTimezone
        })
          .formatToParts(new Date())
          .find((part) => part.type === 'timeZoneName')?.value || userTimezone
      );
    } catch {
      return userTimezone;
    }
  };

  const liveCount = useMemo(
    () => allMatches.filter((m) => m.isLive).length,
    [allMatches]
  );

  if (loading) {
    return (
      <div className="livescores-app">
        <main className="main-content">
          <div className="home-state">…</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="livescores-app">
        <main className="main-content">
          <div className="home-state">
            <p>{error}</p>
            <button type="button" className="home-cta" onClick={fetchMatches}>
              {t.home.retry}
            </button>
          </div>
        </main>
      </div>
    );
  }

  const totalPages = Math.ceil(filteredMatches.length / matchesPerPage) || 1;
  const startIndex = (currentPage - 1) * matchesPerPage;
  const paginatedMatches = filteredMatches.slice(startIndex, startIndex + matchesPerPage);
  const showingCount = Math.min(matchesPerPage, filteredMatches.length - startIndex);

  return (
    <div className="livescores-app">
      <main className="main-content">
        <section className="lf-lobby" aria-label="Welcome">
          <div className="lf-lobby__wrap">
          <button
            type="button"
            className="lf-promo"
            onClick={() => {
              handleTabChange('Live');
              document.querySelector('.matches-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <span className="lf-promo__media" aria-hidden="true">
              <img src="/lobby/promo-bg.webp?v=8" alt="" fetchPriority="high" decoding="async" />
            </span>
            <span className="lf-promo__scrim" aria-hidden="true" />
            <span className="lf-promo__copy">
              <span className="lf-promo__badge">
                <i />
                {liveCount > 0 ? `${liveCount} ${t.home.tileLiveTag}` : t.home.promoKicker}
              </span>
              <strong className="lf-promo__title">{t.home.heroTitle}</strong>
              <em className="lf-promo__text">{t.home.heroText}</em>
              <span className="lf-promo__cta">{t.home.ctaLive}</span>
            </span>
          </button>

          <div className="lf-tiles">
            <button
              type="button"
              className="lf-tile lf-tile--live"
              onClick={() => {
                handleTabChange('Live');
                document.querySelector('.matches-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <strong>{t.home.tileLiveTitle}</strong>
              <span className="lf-tile__go" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <img className="lf-tile__art" src="/lobby/lobby-live.webp?v=16" alt="" aria-hidden="true" loading="lazy" decoding="async" />
            </button>

            <button
              type="button"
              className="lf-tile lf-tile--streams"
              onClick={() => {
                trackEvent(Events.LOBBY_TILE_CLICK, { tile: 'streams' });
                navigate('/streams');
              }}
            >
              <strong>{t.home.tileStreamsTitle}</strong>
              <span className="lf-tile__go" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <img className="lf-tile__art" src="/lobby/lobby-leagues.webp?v=15" alt="" aria-hidden="true" loading="lazy" decoding="async" />
            </button>

            <button
              type="button"
              className="lf-tile lf-tile--scores"
              hidden
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => {
                trackEvent(Events.LOBBY_TILE_CLICK, { tile: 'scores' });
                navigate('/scores');
              }}
            >
              <strong>{t.home.tileScoresTitle}</strong>
              <span className="lf-tile__go" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <img className="lf-tile__art" src="/lobby/lobby-scores.webp?v=15" alt="" aria-hidden="true" loading="lazy" decoding="async" />
            </button>

            <button
              type="button"
              className="lf-tile lf-tile--news"
              onClick={() => {
                trackEvent(Events.LOBBY_TILE_CLICK, { tile: 'news' });
                navigate('/news');
              }}
            >
              <strong>{t.home.tileNewsTitle}</strong>
              <span className="lf-tile__go" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <img className="lf-tile__art" src="/lobby/lobby-news.webp?v=15" alt="" aria-hidden="true" loading="lazy" decoding="async" />
            </button>
          </div>
          </div>
        </section>

        <div className="matches-section">
          <div className="filter-section">
            <div className="filter-tabs">
              {['All', 'Live', 'Soon', 'Finished'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`filter-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab)}
                >
                  {filterLabels[tab]}
                </button>
              ))}
            </div>

            <div className="home-filters">
              <label className="home-filter">
                <span>{t.home.filterDate}</span>
                <select
                  value={dateFilter}
                  onChange={(e) => {
                    trackEvent(Events.HOME_FILTER_CHANGED, {
                      filter: 'date',
                      value: e.target.value
                    });
                    setDateFilter(e.target.value);
                  }}
                >
                  <option value="all">{t.home.allDates}</option>
                  <option value="today">Today</option>
                  {availableDates.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
              <label className="home-filter">
                <span>{t.home.filterLeague}</span>
                <select
                  value={leagueFilter}
                  onChange={(e) => {
                    trackEvent(Events.HOME_FILTER_CHANGED, {
                      filter: 'league',
                      value: e.target.value
                    });
                    setLeagueFilter(e.target.value);
                  }}
                >
                  <option value="all">{t.home.allLeagues}</option>
                  {availableLeagues.map((l) => (
                    <option key={l.name} value={l.name}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="date-info">
                <span className="timezone-display">{getTimezoneAbbr()}</span>
                <span className="home-filter-count">{filteredMatches.length}</span>
              </div>
            </div>
          </div>

          <div className="matches-container">
            {paginatedMatches.length === 0 ? (
              <div className="home-state">{t.home.empty}</div>
            ) : (
              paginatedMatches.map((match) => {
                const kick = formatKickoffDateTime(
                  match.fixture?.date || match.date || match.time,
                  locale
                );
                return (
                  <div
                    key={match.id}
                    className={`match-card${match.isLive ? ' is-live' : ''}`}
                    role="link"
                    tabIndex={0}
                    onClick={() => handleWatchMatch(match.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleWatchMatch(match.id);
                      }
                    }}
                  >
                    {showStarBtn ? (
                      <button
                        type="button"
                        className={`star-btn ${match.isStarred ? 'starred' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStarMatch(match.id);
                        }}
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                            fill={match.isStarred ? 'currentColor' : 'none'}
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                        </svg>
                      </button>
                    ) : null}

                    <span className="time-minute">
                      {match.isLive
                        ? match.minute || match.elapsed || 'LIVE'
                        : (
                            <img
                              src={match.league?.logo || match.leagueLogo || '/default-league-logo.svg'}
                              alt={match.league?.name || 'League'}
                              className="league-logo-inline"
                              title={match.league?.name || 'League'}
                              onError={(e) => {
                                if (!e.target.dataset.fallback) {
                                  e.target.dataset.fallback = '1';
                                  e.target.src = '/default-league-logo.svg';
                                }
                              }}
                            />
                          )}
                    </span>

                    <span className="match-team match-team--home">
                      <span className="team-name">
                        {match.teams?.home?.name || match.homeTeam}
                      </span>
                      <img
                        src={match.teams?.home?.logo || match.homeTeamLogo || match.homeLogo || '/default-team-logo.svg'}
                        alt=""
                        className={`team-logo${!match.teams?.home?.logo && !match.homeTeamLogo && !match.homeLogo ? ' is-placeholder' : ''}`}
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = '1';
                            e.target.src = '/default-team-logo.svg';
                            e.target.classList.add('is-placeholder');
                          }
                        }}
                      />
                    </span>

                    <span className="hf-vs" aria-label="versus">
                      {match.isLive || match.normalizedStatus === 'finished' ? (
                        <>
                          <span className={`hf-score${match.isLive ? ' is-live' : ''}`}>
                            {match.goals?.home != null && match.goals?.away != null
                              ? `${match.goals.home} : ${match.goals.away}`
                              : match.isLive
                                ? 'LIVE'
                                : '– : –'}
                          </span>
                          <span className="hf-vs__time" title={kick.label}>
                            <span className="hf-vs__date">{kick.date}</span>
                            <span className="hf-vs__clock">{kick.time}</span>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="hf-vs__core">
                            <span className="hf-vs__letter">V</span>
                            <span className="hf-vs__slash" />
                            <span className="hf-vs__letter">S</span>
                          </span>
                          <span className="hf-vs__time" title={kick.label}>
                            <span className="hf-vs__date">{kick.date}</span>
                            <span className="hf-vs__clock">{kick.time}</span>
                          </span>
                        </>
                      )}
                    </span>

                    <span className="match-team match-team--away">
                      <img
                        src={match.teams?.away?.logo || match.awayTeamLogo || match.awayLogo || '/default-team-logo.svg'}
                        alt=""
                        className={`team-logo${!match.teams?.away?.logo && !match.awayTeamLogo && !match.awayLogo ? ' is-placeholder' : ''}`}
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = '1';
                            e.target.src = '/default-team-logo.svg';
                            e.target.classList.add('is-placeholder');
                          }
                        }}
                      />
                      <span className="team-name">
                        {match.teams?.away?.name || match.awayTeam}
                      </span>
                    </span>

                    <button
                      type="button"
                      className="tv-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWatchMatch(match.id);
                      }}
                    >
                      {t.home.watch}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {filteredMatches.length ? startIndex + 1 : 0}-
                {filteredMatches.length ? startIndex + showingCount : 0} of{' '}
                {filteredMatches.length} matches
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => {
                    const next = Math.max(1, currentPage - 1);
                    trackEvent(Events.HOME_PAGINATION, { page: next, direction: 'prev' });
                    setCurrentPage(next);
                  }}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => {
                    const next = Math.min(totalPages, currentPage + 1);
                    trackEvent(Events.HOME_PAGINATION, { page: next, direction: 'next' });
                    setCurrentPage(next);
                  }}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HomePage;
